import { useEffect, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";

import StatTile from "shell/StatTile";
import WeeklyAttendanceChart from "shell/WeeklyAttendanceChart";
import WeeklyTitheWelfareChart from "shell/WeeklyTitheWelfareChart";

/**
 * Pastor dashboard — congregation-wide view (members, attendance, giving),
 * mirroring AdminDashboard's data but without the SMS/operational tiles
 * that aren't a pastor's concern.
 */
export default function PastorDashboard() {
  const theme = useTheme();
  const { church, user } = useAuth();
  const [stats, setStats] = useState({
    members: { total: 0 },
    attendance: { today: 0 },
    offerings: { total: 0 },
  });
  const [weeklyAttendance, setWeeklyAttendance] = useState([]);
  const [weeklyTitheWelfare, setWeeklyTitheWelfare] = useState([]);

  useEffect(() => {
    if (!church?.id) return;
    const token = localStorage.getItem("authToken");
    const headers = { Accept: "application/json", Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API_BASE}/analytics/members-count?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
      fetch(`${API_BASE}/analytics/attendance-count?church_id=${church.id}`, { headers }).then(
        (r) => r.json()
      ),
      fetch(`${API_BASE}/analytics/offering-count?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
    ])
      .then(([m, a, o]) =>
        setStats({
          members: { total: m?.total ?? 0 },
          attendance: { today: a?.today ?? 0 },
          offerings: { total: o?.this_week ?? o?.total ?? 0 },
        })
      )
      .catch(() => {});

    fetch(`${API_BASE}/analytics/weekly-attendance?church_id=${church.id}`, { headers })
      .then((r) => r.json())
      .then((rows) => setWeeklyAttendance(Array.isArray(rows) ? rows : []))
      .catch(() => {});

    fetch(`${API_BASE}/analytics/weekly-tithe-welfare?church_id=${church.id}`, { headers })
      .then((r) => r.json())
      .then((rows) => setWeeklyTitheWelfare(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, [church?.id]);

  const greeting = greetingFor(new Date());

  return (
    <Box>
      {/* Hero */}
      <Card
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3.5 },
          background: `linear-gradient(135deg, ${theme.palette.teal[600]} 0%, ${theme.palette.teal[400]} 100%)`,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: -40,
            top: -40,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <Typography variant="overline" sx={{ opacity: 0.9, color: "white" }}>
          {greeting}, {firstName(user?.name)}
        </Typography>
        <Typography variant="h2" sx={{ color: "white", mt: 0.5 }}>
          {church?.name || "Your Church"}
        </Typography>
        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            icon={<Icon sx={{ color: "white !important", fontSize: 16 }}>group</Icon>}
            label={`${stats.members.total.toLocaleString()} members`}
            sx={pillSx}
          />
          <Chip
            icon={<Icon sx={{ color: "white !important", fontSize: 16 }}>how_to_reg</Icon>}
            label={`${stats.attendance.today} present today`}
            sx={pillSx}
          />
        </Box>
      </Card>

      {/* Stat tiles */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatTile
            icon="group"
            label="Total Members"
            value={stats.members.total.toLocaleString()}
            sub="across all departments"
            tone="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatTile
            icon="how_to_reg"
            label="Attendance today"
            value={stats.attendance.today.toLocaleString()}
            sub="checked-in this service"
            tone="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatTile
            icon="payments"
            label="This week's offerings"
            value={`GHS ${Number(stats.offerings.total).toLocaleString()}`}
            sub="tithes + welfare + offering"
            tone="success"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h5">Weekly attendance</Typography>
              <Chip
                size="small"
                label="Last 7 days"
                sx={{ bgcolor: theme.palette.ink[100], color: "text.secondary" }}
              />
            </Box>
            <WeeklyAttendanceChart data={weeklyAttendance} />
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mt: 2.5 }}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h5">Tithe &amp; Welfare</Typography>
              <Chip
                size="small"
                label="Last 7 days"
                sx={{ bgcolor: theme.palette.ink[100], color: "text.secondary" }}
              />
            </Box>
            <WeeklyTitheWelfareChart data={weeklyTitheWelfare} />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

const pillSx = {
  bgcolor: "rgba(255,255,255,0.18)",
  color: "white",
  fontWeight: 600,
  border: "1px solid rgba(255,255,255,0.18)",
  "& .MuiChip-icon": { ml: 0.5 },
};

function greetingFor(d) {
  const h = d.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
function firstName(name) {
  return (name || "Pastor").split(" ")[0];
}
